<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import { Float } from "@headlessui-float/vue";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
} from "@heroicons/vue/20/solid";
import { useQueryClient } from "@tanstack/vue-query";
import { inject } from "vue";
import { useToast } from "vue-toastification";
import useRole from "../composables/useRole";
import { Role } from "../composables/useRoles";

interface Props {
  role: Role;
  showMembers?: boolean;
  showEdit?: string;
  abilityEdit?: string;
  abilityDelete?: string;
  module?: string;
}

const toast = useToast();

interface Emits {
  (e: "deleteRole", id: string | number): Promise<void>;
}

const emits = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
  showMembers: true,
  selected: false,
  showEdit: "roles.edit",
});

const { hasPermissionUser } = useHasPermissionUser();

const { invalidateQueryData } = inject("invalidateQueryData") as {
  invalidateQueryData: () => Promise<void>;
};

const handleDelete = async (id: string | number) => {
  await emits("deleteRole", id);

  toast.success("Rol eliminado correctamente");
  await invalidateQueryData();
};
</script>

<template>
  <div class="grid grid-cols-3 gap-5 border-b-2">
    <RouterLink
      :to="{
        name: showEdit,
        params: { id: role.id },
        state: {
          pages: [
            { name: 'Roles', to: '/roles', current: false },
            {
              name: `Editar (${role.name})`,
              to: `/roles/${role.id}/edit`,
              current: true,
            },
          ],
        },
      }"
      :class="[
        hasPermissionUser(abilityEdit || '', module || '')
          ? 'cursor-pointer'
          : 'pointer-events-none',
      ]"
    >
      <h3 class="p-3">{{ role.name }}</h3>
    </RouterLink>

    <figure v-if="showMembers" class="flex items-center justify-center">
      <p>{{ role.usersCount }}</p>
      <span class="material-symbols-outlined"> group </span>
    </figure>

    <div v-if="role.name !== 'Master'" class="mt-2">
      <Menu
        as="div"
        class="flex items-center justify-center"
        v-if="
          hasPermissionUser(abilityEdit || '', module || '') ||
          hasPermissionUser(abilityDelete || '', module || '')
        "
      >
        <Float
          strategy="fixed"
          placement="bottom-end"
          :offset="4"
          enter="transition ease-out duration-100"
          enter-from="transform opacity-0 scale-95"
          enter-to="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leave-from="transform opacity-100 scale-100"
          leave-to="transform opacity-0 scale-95"
        >
          <MenuButton
            class="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-200 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
          >
            <EllipsisVerticalIcon
              class="h-5 w-5 text-gray-800"
              aria-hidden="true"
            />
          </MenuButton>
          <MenuItems
            class="w-56 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            <div class="py-1">
              <HasPermission
                :name-permission="abilityEdit || ''"
                :entity-type="module || ''"
              >
                <MenuItem v-slot="{ active }">
                  <RouterLink
                    :to="{
                      name: showEdit,
                      params: { id: role.id },
                    }"
                    :class="[
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                      'group flex items-center px-4 py-2 text-sm',
                    ]"
                  >
                    <PencilSquareIcon
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Editar
                  </RouterLink>
                </MenuItem>
              </HasPermission>

              <MenuItem v-slot="{ active }">
                <button
                  class="w-full"
                  :class="[
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                    'group flex items-center px-4 py-2  text-sm gap-2',
                  ]"
                  @click="handleDelete(role.id)"
                >
                  <small class="material-symbols-outlined text-gray-400">
                    delete
                  </small>
                  <p>Eliminar</p>
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </Float>
      </Menu>
    </div>
  </div>
</template>

<style scoped></style>
